// A section's hire lines (the template's rows 69–76): items from the hire list
// and quoted items, days × day rate + trips × cartage (+ fuel), marked up at
// "other". A rate typed for a hire-list item, and a quoted item, are the job's
// prices (#413): every area and option on the measure uses them. Older lines
// typed before the job price book keep their own rates.
import { addAreaLine, hireLine, quotedHire, removeAreaLine, updateAreaLine, type AccessChoice, type HireList, type PricingRates } from '@higgins/m2q-pricing';
import type { JobRowActions } from '../lib/job-prices';
import type { HireItemFromApi } from '../lib/types';
import { NumberField } from './number-field';
import { SavedTextField, TEXT_MAX } from './saved-text-field';
import { formatMoney, formatPercent, formatTwo } from './surface-format';
import { CHANGED } from './take-off-rows';
import { AddFromList, JobPriceTag, Row, type Edit } from './take-off-extra-row';
import { small } from './take-off-small-number';
import { SupplierQuoteControl } from './supplier-quote';

const DAYS_PER_WEEK = 7;
const NEW_QUOTED = '__new_quoted__';
const QUOTED_PREFIX = 'job:';

interface Props {
  areaId: string;
  optionId: string;
  lines: readonly AccessChoice[];
  rates: PricingRates;
  hireItems: readonly HireItemFromApi[];
  job: JobRowActions;
  onScope: Edit;
}

/** The day-rate and cartage boxes: the job's price for the item (or the quoted item's), or a rate typed on an older line. */
function RateBoxes({ line, at, item, job, set }: { line: AccessChoice; at: ReturnType<typeof hireLine>; item: HireItemFromApi | undefined; job: JobRowActions; set: (change: Record<string, unknown>) => void }) {
  const quoted = line.jobPriceId ? job.prices.find((p) => p.id === line.jobPriceId) : undefined;
  const jobRow = quoted ?? (item ? job.prices.find((p) => p.hireItemId?.toLowerCase() === item.id.toLowerCase()) : undefined);
  const write = (field: 'hig_dayrate' | 'hig_transport', lineField: 'dayRate' | 'transport', value: number | null) => {
    if (quoted) job.onRow(quoted.id, { [field]: value ?? 0 });
    else if (item) job.onHirePrice(item, { [field]: value });
    else set({ [lineField]: value });
  };
  const box = (field: 'hig_dayrate' | 'hig_transport', lineField: 'dayRate' | 'transport', label: string) => {
    const typed = jobRow ? (field === 'hig_dayrate' ? jobRow.dayRate : jobRow.transport) : (line[lineField] ?? null);
    const listed = item ? (lineField === 'dayRate' ? item.dayRate : item.transport) : (at?.[lineField] ?? 0);
    return <NumberField compact value={typed} placeholder={formatTwo(listed)} onChange={(v) => write(field, lineField, v)} label={label} className={`w-16 ${typed !== null && jobRow ? CHANGED : ''}`} />;
  };
  return (
    <>
      {small(line.days, (v) => set({ days: v ?? 0 }), 'Days')} days ×{box('hig_dayrate', 'dayRate', 'Day rate')}
      $/day + {small(line.trips, (v) => set({ trips: v ?? 0 }), 'Cartage trips')} trips ×{box('hig_transport', 'transport', 'Cartage $')}$
    </>
  );
}

export function HireRows({ areaId, optionId, lines, rates, hireItems, job, onScope }: Props) {
  const markup = 1 + rates.markupOther;
  const hire: HireList = new Map(hireItems.map((h) => [h.id, h]));
  const quotedItems = quotedHire(job.prices);

  function addHire(value: string) {
    if (value === NEW_QUOTED) {
      void job.onAddQuotedHire().then((jobPriceId) => onScope((s) => addAreaLine(s, areaId, 'access', { jobPriceId, days: 1, trips: 0 })));
      return;
    }
    const line = value.startsWith(QUOTED_PREFIX) ? { jobPriceId: value.slice(QUOTED_PREFIX.length), days: 1, trips: 0 } : { hireItemId: value, days: 1, trips: 1 };
    onScope((s) => addAreaLine(s, areaId, 'access', line));
  }

  return (
    <>
      {lines.map((line, i) => {
        const at = hireLine(line, hire, job.prices);
        const item = line.hireItemId ? hire.get(line.hireItemId) : undefined;
        const quoted = line.jobPriceId ? job.prices.find((p) => p.id === line.jobPriceId) : undefined;
        const sell = at ? (at.days * at.dayRate + at.trips * at.transport + (at.days / DAYS_PER_WEEK) * at.fuelPerWeek) * markup : 0;
        const set = (change: Record<string, unknown>) => onScope((s) => updateAreaLine(s, areaId, 'access', i, change));
        const diesel = quoted ? quoted.diesel : line.diesel === true;
        const setDiesel = (on: boolean) => (quoted ? job.onRow(quoted.id, { hig_diesel: on }) : set({ diesel: on || null }));
        return (
          <Row key={`h${i}`} sell={sell} removal={{ question: `Remove ${at?.item ?? line.item ?? 'this hire line'} from this area's price?`, remove: () => onScope((s) => removeAreaLine(s, areaId, 'access', i)) }}>
            {quoted ? (
              <SavedTextField compact value={quoted.name} onSave={(name) => job.onRow(quoted.id, { hig_name: name })} label="Quoted item" maxLength={TEXT_MAX} className="w-48" />
            ) : line.hireItemId ? (
              <span className={at ? '' : 'text-red-700'}>{at ? at.item : `${line.item ?? 'Hire item'} — no rate on the pricing date`}</span>
            ) : (
              <SavedTextField compact value={line.item ?? ''} onSave={(name) => set({ item: name })} label="Quoted item" maxLength={TEXT_MAX} className="w-48" />
            )}
            {at && 'jobPrice' in at && <JobPriceTag />}
            <RateBoxes line={line} at={at} item={item} job={job} set={set} />
            {!line.hireItemId && (
              <label className="flex items-center gap-1 text-xs text-slate-600">
                <input type="checkbox" checked={diesel} onChange={(e) => setDiesel(e.target.checked)} className="h-4 w-4" />
                Diesel?
              </label>
            )}
            {(at?.fuelPerWeek ?? 0) > 0 && <span className="text-xs text-slate-500">+ fuel {formatMoney(at?.fuelPerWeek ?? 0)}/wk × days ÷ 7</span>}
            {diesel && !line.hireItemId && at?.fuelPerWeek === 0 && <span className="text-xs text-red-700">no fuel allowance on the hire list on the pricing date</span>}
            <span className="text-xs text-slate-500">+ {formatPercent(rates.markupOther)}</span>
            <SupplierQuoteControl optionId={optionId} quote={line.quote} lineName={at?.item ?? quoted?.name ?? line.item ?? ''} onQuote={(quote) => set({ quote })} />
          </Row>
        );
      })}
      <AddFromList label="Add hire" onPick={addHire}>
        {hireItems.map((h) => (
          <option key={h.id} value={h.id}>
            {h.name} ({h.supplier}) — {formatMoney(h.dayRate)}/day
          </option>
        ))}
        {quotedItems.map((q) => (
          <option key={q.id} value={`${QUOTED_PREFIX}${q.id}`}>
            {q.name} (quoted for this job) — {formatMoney(q.dayRate ?? 0)}/day
          </option>
        ))}
        <option value={NEW_QUOTED}>New quoted item (its rates apply to the whole job)</option>
      </AddFromList>
    </>
  );
}
